import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import Field from '@/components/common/Field.jsx';
import PageHeader from '@/components/common/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import { actualizarConfiguracionAlertaOperativa, fetchConfiguracionAlertasOperativas } from '@/services/inteligenciaOperativa.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

export default function InteligenciaOperativaConfiguracion() {
  const { sucursal } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    fetchConfiguracionAlertasOperativas(sucursal?.id)
      .then((data) => {
        if (!active) return;
        setRows(data);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sucursal?.id]);

  const updateRow = (codigoRegla, updater) => {
    setRows((prev) => prev.map((row) => (row.codigoRegla === codigoRegla ? updater(row) : row)));
  };

  const saveAll = async () => {
    setSaving(true);
    setMessage('');
    try {
      for (const row of rows) {
        await actualizarConfiguracionAlertaOperativa({
          sucursalId: sucursal?.id || null,
          codigoRegla: row.codigoRegla,
          activa: Boolean(row.activa),
          severidadDefault: row.severidadDefault,
          parametros: row.parametros || {},
        });
      }
      setMessage('Configuración guardada.');
    } catch (err) {
      setMessage(err?.message || 'No se pudo guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4">
      <PageHeader
        title="Configuración de Inteligencia Operativa"
        description="Parámetros auditables y mantenibles para ajustar umbrales, activación y severidad de las reglas."
        breadcrumbs={[
          { label: 'Operaciones', href: '/monitor/operaciones' },
          { label: 'Inteligencia Operativa', href: '/operaciones/inteligencia' },
          { label: 'Configuración' },
        ]}
        actions={<Link to="/operaciones/inteligencia" className="rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm font-semibold">Volver al tablero</Link>}
      />

      <section className="rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgb(var(--panel))] p-4 shadow-[0_18px_36px_rgba(var(--shadow),0.08)]">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5 text-[rgb(var(--brand))]" />
          <h2 className="text-lg font-semibold">Reglas activas y parámetros</h2>
        </div>
        {loading ? <p className="mt-4 text-sm text-[rgba(var(--muted),0.9)]">Cargando configuración…</p> : null}
        <div className="mt-4 grid grid-cols-12 gap-4">
          {rows.map((row) => (
            <article key={row.codigoRegla} className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgb(var(--subpanel))] p-4 xl:col-span-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold">{row.etiqueta || row.codigoRegla}</h3>
                  <p className="mt-1 text-sm text-[rgba(var(--muted),0.95)]">{row.descripcion || row.codigoRegla}</p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm font-semibold">
                  <input type="checkbox" checked={Boolean(row.activa)} onChange={(e) => updateRow(row.codigoRegla, (current) => ({ ...current, activa: e.target.checked }))} />
                  Activa
                </label>
              </div>
              <div className="mt-4 grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-4">
                  <Field label="Severidad">
                    <select value={row.severidadDefault} onChange={(e) => updateRow(row.codigoRegla, (current) => ({ ...current, severidadDefault: e.target.value }))} className="w-full rounded-xl bg-[rgb(var(--panel))] px-3 py-2">
                      <option value="CRITICA">Crítica</option>
                      <option value="ALTA">Alta</option>
                      <option value="MEDIA">Media</option>
                      <option value="BAJA">Baja</option>
                    </select>
                  </Field>
                </div>
                {Object.entries(row.parametros || {}).map(([key, value]) => (
                  <div key={`${row.codigoRegla}-${key}`} className="col-span-12 md:col-span-4">
                    <Field label={key.replace(/_/g, ' ')}>
                      <input
                        type="number"
                        value={value}
                        onChange={(e) =>
                          updateRow(row.codigoRegla, (current) => ({
                            ...current,
                            parametros: {
                              ...current.parametros,
                              [key]: Number(e.target.value),
                            },
                          }))
                        }
                        className="w-full rounded-xl bg-[rgb(var(--panel))] px-3 py-2"
                      />
                    </Field>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
        {message ? <p className="mt-4 text-sm font-medium text-[rgb(var(--brand-700))]">{message}</p> : null}
        <StickyActions
          onCancel={() => window.history.back()}
          onSubmit={saveAll}
          isSubmitting={saving}
          primaryLabel="Guardar configuración"
          secondaryLabel="Volver"
        />
      </section>
    </div>
  );
}
